import RebusProgressBar from '../common/RebusProgressBar';

/**
 * Team progress overview showing score, station completion, and time remaining.
 *
 * @param {Object} props
 * @param {Object} props.rebus - Full rebus data (stations, extraTasks, settings)
 * @param {Object} props.session - Current session data (teams, status, etc.)
 * @param {string} props.teamId - Current team ID
 */
export default function TeamProgress({ rebus, session, teamId }) {
  if (!rebus || !session || !teamId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <span className="material-symbols-outlined text-5xl mb-3">hourglass_empty</span>
        <p className="text-lg font-semibold">Lastar...</p>
      </div>
    );
  }

  const team = session.teams?.[teamId];
  if (!team) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <span className="material-symbols-outlined text-5xl mb-3">error_outline</span>
        <p className="text-lg font-semibold">Fann ikkje laget</p>
      </div>
    );
  }

  const stations = rebus.stations || [];
  const extraTasks = rebus.extraTasks || [];
  const stationResults = team.stationResults || {};
  const extraTaskResults = team.extraTaskResults || {};

  // Count completed stations
  const completedStations = stations.filter(
    (s) => stationResults[s.id]?.completedAt
  ).length;

  // Calculate live score from station answers + challenge scores + approved extra tasks
  let calculatedScore = 0;

  // Station answers
  for (const stationId of Object.keys(stationResults)) {
    const sr = stationResults[stationId];
    const answers = sr.answers || {};
    for (const qId of Object.keys(answers)) {
      calculatedScore += answers[qId].points || 0;
    }
    if (typeof sr.challengeScore === 'number') {
      calculatedScore += sr.challengeScore;
    }
  }

  // Approved extra tasks
  for (const taskId of Object.keys(extraTaskResults)) {
    const tr = extraTaskResults[taskId];
    if (tr.approved === true) {
      calculatedScore += tr.points || 0;
    }
  }

  // Bonus points
  if (typeof team.bonusPoints === 'number') {
    calculatedScore += team.bonusPoints;
  }

  const totalScore = calculatedScore || team.totalScore || 0;

  // Count submitted extra tasks
  const submittedExtraTasks = Object.keys(extraTaskResults).length;

  // Time remaining calculation
  const getTimeRemaining = () => {
    const deadline = rebus.settings?.deadline;
    if (!deadline) return null;

    const deadlineTime = new Date(deadline).getTime();
    const now = Date.now();
    const diff = deadlineTime - now;

    if (diff <= 0) return { text: 'Tida er ute!', expired: true };

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return { text: `${hours} t ${minutes} min att`, expired: false };
    }
    return { text: `${minutes} min att`, expired: false };
  };

  const timeRemaining = getTimeRemaining();

  return (
    <div className="pb-28 space-y-5">
      {/* Team header with score */}
      <div className="bg-gradient-to-br from-teal-600 to-emerald-600 text-white rounded-2xl p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <span className="material-symbols-outlined text-3xl text-white/80">groups</span>
          <h2 className="font-extrabold text-xl truncate">{team.name}</h2>
        </div>

        {/* Big score */}
        <div className="text-center py-4">
          <p className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-1">
            Total poengsum
          </p>
          <p className="text-6xl font-extrabold tabular-nums">{totalScore}</p>
        </div>

        {/* Time remaining */}
        {timeRemaining && (
          <div className={`mt-3 flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2 rounded-xl ${
            timeRemaining.expired
              ? 'bg-red-500/30 text-red-100'
              : 'bg-white/15 text-white/90'
          }`}>
            <span className="material-symbols-outlined text-lg">
              {timeRemaining.expired ? 'timer_off' : 'timer'}
            </span>
            <span>{timeRemaining.text}</span>
          </div>
        )}
      </div>

      {/* Station progress */}
      <div className="bg-white rounded-2xl p-5 shadow-ambient">
        <h3 className="font-extrabold text-gray-900 mb-3 flex items-center gap-2">
          <span className="material-symbols-outlined text-teal-600">location_on</span>
          Stasjonar
        </h3>
        <RebusProgressBar completed={completedStations} total={stations.length} />

        {/* Station list */}
        <div className="mt-4 space-y-2">
          {stations.map((station) => {
            const sr = stationResults[station.id];
            const isCompleted = !!sr?.completedAt;
            const answeredCount = Object.keys(sr?.answers || {}).length;
            const totalQuestions = station.questions?.length || 0;

            return (
              <div
                key={station.id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${
                  isCompleted ? 'bg-emerald-50' : 'bg-gray-50'
                }`}
              >
                {isCompleted ? (
                  <span className="material-symbols-outlined text-xl text-emerald-500">check_circle</span>
                ) : answeredCount > 0 ? (
                  <span className="material-symbols-outlined text-xl text-amber-500">pending</span>
                ) : (
                  <span className="material-symbols-outlined text-xl text-gray-300">radio_button_unchecked</span>
                )}
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-sm truncate ${
                    isCompleted ? 'text-emerald-700' : 'text-gray-800'
                  }`}>
                    {station.name || `Stasjon ${station.order || ''}`}
                  </p>
                  {totalQuestions > 0 && !isCompleted && (
                    <p className="text-xs text-gray-500">
                      {answeredCount}/{totalQuestions} spørsmål svart
                    </p>
                  )}
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'
                }`}>
                  {station.order || ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Extra tasks summary */}
      {extraTasks.length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-ambient">
          <h3 className="font-extrabold text-gray-900 mb-3 flex items-center gap-2">
            <span className="material-symbols-outlined text-teal-600">assignment</span>
            Ekstraoppgåver
          </h3>
          <RebusProgressBar
            completed={submittedExtraTasks}
            total={extraTasks.length}
            label={`${submittedExtraTasks} av ${extraTasks.length} levert`}
          />

          <div className="mt-3 grid grid-cols-2 gap-2">
            {/* Approved count */}
            <div className="bg-emerald-50 rounded-xl px-3 py-2 text-center">
              <p className="text-xl font-extrabold text-emerald-600">
                {Object.values(extraTaskResults).filter((r) => r.approved === true).length}
              </p>
              <p className="text-xs font-semibold text-emerald-700">Godkjent</p>
            </div>
            {/* Pending count */}
            <div className="bg-amber-50 rounded-xl px-3 py-2 text-center">
              <p className="text-xl font-extrabold text-amber-600">
                {Object.values(extraTaskResults).filter((r) => r.approved === null).length}
              </p>
              <p className="text-xs font-semibold text-amber-700">Ventar</p>
            </div>
          </div>
        </div>
      )}

      {/* Session status */}
      <div className="bg-gray-50 rounded-2xl px-5 py-3 text-center">
        <p className="text-xs text-gray-500 font-medium">
          Rebus-økt: <span className="font-bold text-gray-700">{session.id}</span>
          <span className="mx-2">&bull;</span>
          Status: <span className="font-bold text-gray-700">
            {session.status === 'active' ? 'Aktiv' : session.status === 'waiting' ? 'Ventar' : 'Avslutta'}
          </span>
        </p>
      </div>
    </div>
  );
}
